import { forwardRef, useRef, useLayoutEffect, useContext, useMemo, useState } from 'react';
import type { PropsWithoutRef, Ref, FocusEvent } from 'react';
import styled, { css } from 'styled-components';
import { transparentize } from 'polished';

import type { ForwardRefForwardPropsComponent } from '../../types';
import Button from '../Button';
import Icon, { registerIcon } from '../Icon';
import * as plusIcon from '../Icon/icons/plus.icon';
import { useDirection, useI18n, useLiveLog } from '../../hooks';
import { defaultThemeProp } from '../../theme';
import { getActiveElement, getFocusables, tryCatch } from '../../utils';
import Flex from '../Flex';
import EmptyState from '../EmptyState';
import AdditionalInfo from '../AdditionalInfo';
import Text from '../Text';
import { supplementalColors, readableHue } from '../../styles';

import { getIndexDetails } from './FieldGroupList.utils';
import FieldGroup, {
  StyledFieldGroup,
  StyledFieldGroupContent,
  StyledFieldGroupLegend,
  StyledGroupContainer,
  StyledFieldHeader
} from './FieldGroup';
import type { FieldGroupProps } from './FieldGroup';
import FieldGroupListContext from './FieldGroupListContext';

registerIcon(plusIcon);

export type FieldGroupListItemProps = FieldGroupProps & {
  /** The ID of the field group list item */
  id: string;
  /** Item level onDelete callback. */
  onDelete?: (id: string) => void;
  /** Indicates if the item is dynamic. */
  isDynamic?: boolean;
};

export interface FieldGroupListProps {
  items: FieldGroupListItemProps[];
  contextualLabel?: string;
  label?: string;
  additionalContext?: string | JSX.Element;
  onAdd?: () => void;
  onDelete?: (id: string) => void;
  ref?: Ref<HTMLOListElement>;
}

export interface ListItemProps {
  item: FieldGroupListItemProps;
  index: number;
  isAddFocused: boolean;
  handleDelete: FieldGroupListProps['onDelete'];
  fieldGroupRef: (el: HTMLFieldSetElement | null) => void;
}

const StyledFieldGroupList = styled.ol(
  () => css`
    list-style: none;
  `
);

StyledFieldGroupList.defaultProps = defaultThemeProp;

export const StyledListItem = styled.li<{
  depth: number;
  listGroupIndex: number;
  shouldHighlight: boolean;
}>(({ theme, depth, listGroupIndex, shouldHighlight }) => {
  const spacing = theme.base.spacing;
  const palette = theme.base.palette;
  const borderRadius = theme.base['border-radius'];
  const groupColors = theme.components['field-group-list']['group-colors'];
  const supplementalColorList = supplementalColors(theme);

  const bgColor = groupColors
    ? tryCatch(
        () =>
          transparentize(
            0.75,
            readableHue(
              supplementalColorList[listGroupIndex % supplementalColorList.length],
              palette['app-background']
            )
          ),
        () => theme.base.palette['border-line']
      )
    : theme.base.palette['border-line'];

  return css`
    display: block;

    > ${StyledGroupContainer} {
      position: relative;
      padding-inline-end: ${spacing};

      &::before {
        content: '';
        position: absolute;
        inset-block-start: 0;
        bottom: 0;
        inset-inline-start: 0;
        width: calc(0.5 * ${spacing});
        background: ${bgColor};
      }

      ${depth > 1 &&
      css`
        margin-inline-end: -${spacing};
      `}

      ${shouldHighlight &&
      css`
        border-start-end-radius: calc(0.5 * ${borderRadius});
        border-end-end-radius: calc(0.5 * ${borderRadius});
        background: ${transparentize(0.95, palette['foreground-color'])};
      `}
    }

    > ${StyledGroupContainer} > ${StyledFieldGroup} {
      margin-block-start: 0;
      padding: ${spacing} 0;

      &:not(:last-of-type) {
        border-bottom: 0.0625rem solid ${palette['border-line']};
        margin-block-end: calc(2 * ${spacing});
      }

      legend {
        margin-block-end: 0;
      }
    }

    > ${StyledGroupContainer}
      > ${StyledFieldGroup}
      > ${StyledFieldGroupContent},
      > ${StyledGroupContainer}
      > ${StyledFieldGroup}
      > div
      > ${StyledFieldGroupContent} {
      padding-inline-start: calc(1.5 * ${spacing});
    }

    > ${StyledGroupContainer} > ${StyledFieldGroup} > ${StyledFieldGroupLegend} {
      ${StyledFieldHeader} {
        margin-inline-start: calc(0.5 * ${spacing});
        padding-inline: ${spacing};
        height: calc(4 * ${spacing});
        border-start-end-radius: calc(0.5 * ${borderRadius});
        border-end-end-radius: calc(0.5 * ${borderRadius});
        background: ${bgColor};
      }
    }
  `;
});

StyledListItem.defaultProps = defaultThemeProp;

const StyledAddButton = styled(Button)(
  ({
    theme: {
      base: {
        spacing,
        'hit-area': { 'mouse-min': hitAreaMouse }
      }
    }
  }) => css`
    display: inline-flex;
    align-items: center;
    min-height: ${hitAreaMouse};
    min-width: ${hitAreaMouse};
    width: fit-content;
    margin-inline-start: calc(2 * ${spacing});
  `
);

StyledAddButton.defaultProps = defaultThemeProp;

const StyledFieldGroupListContainer = styled.div<{ hasAdd: boolean }>(
  ({
    theme: {
      base: { spacing, palette, 'border-radius': borderRadius }
    },
    hasAdd
  }) => css`
    position: relative;
    margin-block-start: ${spacing};

    ${hasAdd &&
    css`
      &::before {
        content: '';
        position: absolute;
        inset-inline-start: 0;
        bottom: calc(1.5 * ${spacing});
        width: calc(1.5 * ${spacing});
        height: calc(2.5 * ${spacing});
        border-end-start-radius: calc(0.5 * ${borderRadius});
        border-inline-start: calc(0.5 * ${spacing}) solid ${palette['border-line']};
        border-bottom: calc(0.5 * ${spacing}) solid ${palette['border-line']};
      }
    `}
  `
);

StyledFieldGroupListContainer.defaultProps = defaultThemeProp;

const StyledDivider = styled.div(
  ({
    theme: {
      base: { spacing, palette }
    }
  }) => css`
    flex: 1;
    height: calc(0.5 * ${spacing});
    background-color: ${palette['border-line']};
  `
);

StyledDivider.defaultProps = defaultThemeProp;

const ListItem = ({ item, index, isAddFocused, handleDelete, fieldGroupRef }: ListItemProps) => {
  const { id, children, name = '', isDynamic, headingTag, ...restItemProps } = item;

  const t = useI18n();
  const { rtl } = useDirection();
  const { announcePolite } = useLiveLog();

  const {
    depth = 1,
    ancestorIndices = [],
    isAncestorHighlighted = false
  } = useContext(FieldGroupListContext);

  const [isItemFocused, setIsItemFocused] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);

  const listIndices = [...ancestorIndices, index];
  const { rootIndex, currentIndex, compoundIndex } = getIndexDetails(listIndices);

  const shouldHighlight =
    !isAncestorHighlighted && (isAddFocused || isDeleteHovered || isItemFocused);

  let formattedName: string;
  if (isDynamic) {
    formattedName = name;
  } else {
    formattedName = rtl ? `${currentIndex}\u200F ${name}` : `${currentIndex} ${name}`;
  }
  const contextualLabel = isDynamic ? name : `${name} ${compoundIndex}`;

  const ctxValue = useMemo(
    () => ({
      depth: depth + 1,
      isAncestorHighlighted: shouldHighlight || isAncestorHighlighted,
      ancestorIndices: listIndices
    }),
    [depth, index, shouldHighlight, isAncestorHighlighted, listIndices]
  );

  return (
    <StyledListItem
      depth={depth}
      listGroupIndex={rootIndex}
      shouldHighlight={shouldHighlight}
      onFocus={() => {
        setIsItemFocused(true);
      }}
      onBlur={() => {
        setIsItemFocused(false);
      }}
    >
      <FieldGroupListContext.Provider value={ctxValue}>
        <FieldGroup
          name={formattedName}
          contextualLabel={contextualLabel}
          headingTag={headingTag}
          {...restItemProps}
          actions={
            handleDelete
              ? [
                  {
                    id: 'delete',
                    text: t('delete'),
                    icon: 'trash',
                    iconOnly: true,
                    onClick: () => {
                      handleDelete(id);
                      announcePolite({ message: t('deleted', [contextualLabel]) });
                    },
                    onMouseEnter: () => setIsDeleteHovered(true),
                    onMouseLeave: () => setIsDeleteHovered(false)
                  }
                ]
              : undefined
          }
          ref={fieldGroupRef}
        >
          {children}
        </FieldGroup>
      </FieldGroupListContext.Provider>
    </StyledListItem>
  );
};

const FieldGroupList: ForwardRefForwardPropsComponent<FieldGroupListProps> = forwardRef(
  function FieldGroupList(
    {
      label,
      additionalContext,
      items,
      onAdd,
      onDelete,
      contextualLabel,
      ...restProps
    }: PropsWithoutRef<FieldGroupListProps>,
    ref: FieldGroupListProps['ref']
  ) {
    const t = useI18n();

    const addBtnRef = useRef<HTMLButtonElement>(null);
    const itemRefs = useRef<(HTMLFieldSetElement | null)[]>([]);
    const deletedItemIndexRef = useRef<number | null>(null);

    const [isAddFocused, setIsAddFocused] = useState(false);

    const focusElement = (
      itemRef: HTMLFieldSetElement | null,
      focusPreference: 'action' | 'content'
    ) => {
      if (!itemRef) return;

      const focusables = getFocusables(itemRef);
      const groupItemLegend = itemRef.querySelector(':scope > legend');

      const groupActionButtons = focusables.filter(focusable =>
        groupItemLegend?.contains(focusable)
      );

      const groupContentFocusables = focusables.filter(
        focusable => !groupItemLegend?.contains(focusable)
      );

      if (focusPreference === 'action') {
        (groupActionButtons[0] || groupContentFocusables[0])?.focus();
      } else {
        (groupContentFocusables[0] || groupActionButtons[0])?.focus();
      }
    };

    useLayoutEffect(() => {
      const currentActiveEl = getActiveElement();

      if (deletedItemIndexRef.current !== null) {
        const deletedIndex = deletedItemIndexRef.current;

        if (items.length === 0 && addBtnRef.current) {
          addBtnRef.current.focus();
        } else {
          const nextIndex = Math.min(deletedIndex, items.length - 1);
          const nextItemRef = itemRefs.current[nextIndex];
          focusElement(nextItemRef, 'action');
        }

        deletedItemIndexRef.current = null;
      }

      if (currentActiveEl === addBtnRef.current) {
        const lastIndex = items.length - 1;
        const lastItemRef = itemRefs.current[lastIndex];
        focusElement(lastItemRef, 'content');
      }
    }, [items.length]);

    return (
      <Flex
        container={{ direction: 'column', gap: 1 }}
        as={StyledFieldGroupListContainer}
        hasAdd={!!onAdd}
        onFocus={(e: FocusEvent) => {
          e.stopPropagation();
        }}
        onBlur={(e: FocusEvent) => {
          e.stopPropagation();
        }}
      >
        {label && (
          <Flex container={{ gap: 0.5, alignItems: 'center' }}>
            <Text variant='h3'>{label}</Text>

            {additionalContext && (
              <AdditionalInfo contextualLabel={label}>{additionalContext}</AdditionalInfo>
            )}

            <StyledDivider aria-hidden='true' />
          </Flex>
        )}

        {items.length === 0 ? (
          <EmptyState />
        ) : (
          <Flex
            container={{ direction: 'column', gap: 1 }}
            as={StyledFieldGroupList}
            ref={ref}
            aria-label={label}
            {...restProps}
          >
            {items.map((item, index) => {
              const handleDelete =
                onDelete || item.onDelete
                  ? (id: string) => {
                      deletedItemIndexRef.current = index;
                      item.onDelete?.(id);
                      onDelete?.(id);
                    }
                  : undefined;

              return (
                <ListItem
                  key={item.id}
                  item={item}
                  index={index}
                  isAddFocused={isAddFocused}
                  handleDelete={handleDelete}
                  fieldGroupRef={(el: HTMLFieldSetElement | null) => {
                    itemRefs.current[index] = el;
                  }}
                />
              );
            })}
          </Flex>
        )}

        {onAdd && (
          <StyledAddButton
            variant='link'
            onClick={onAdd}
            onFocus={() => {
              setIsAddFocused(true);
            }}
            onBlur={() => setIsAddFocused(false)}
            ref={addBtnRef}
          >
            <Icon name='plus' />
            {contextualLabel ? t('add_noun', [contextualLabel]) : t('add')}
          </StyledAddButton>
        )}
      </Flex>
    );
  }
);

export default FieldGroupList;
